import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Notification } from '@components/notification';
import { Toast, ToastContextType, ToastProviderProps } from '@contexts/toast/toast.type';

export const DEFAULT_INTERVAL = 5000;
// Success toasts auto-dismiss faster: the message is throw-away confirmation and
// otherwise lingers after the user has already moved on.
export const SUCCESS_INTERVAL = 3000;

export const ToastContext = createContext<ToastContextType>({
  data: [],
  push: () => {},
  remove: () => {},
});

const ToastContainer = () => {
  const toastContext = useContext(ToastContext);

  function handleRemove(id: string) {
    toastContext.remove(id);
  }

  return (
    <div
      aria-live='assertive'
      className='fixed z-50 inset-0 flex flex-col justify-start items-center space-y-8 px-4 py-6 pointer-events-none sm:items-end sm:p-6'>
      {toastContext.data.map((toast) => {
        return (
          <Notification
            type={toast.type}
            key={toast.id}
            id={toast.id}
            title={toast.title}
            text={toast.text}
            onRemove={handleRemove}
            lifetime={toast.lifetime}
            disableTimeOut={toast.disableTimeOut}
          />
        );
      })}
    </div>
  );
};

export const ToastProvider = ({ children }: ToastProviderProps) => {
  const [data, setData] = useState<Array<Toast>>([]);

  const Push = useCallback(
    ({ title, text, lifetime, type, disableTimeOut = false }: Toast) => {
      const defaultLifetime = type === 'success' ? SUCCESS_INTERVAL : DEFAULT_INTERVAL;
      const newItem: Toast = {
        id: globalThis.crypto.randomUUID(),
        title,
        text,
        type,
        lifetime: lifetime ?? defaultLifetime,
        disableTimeOut,
      };

      setData((prevState) => [...prevState.filter((toast) => toast.title !== title), newItem]);
    },
    [setData]
  );

  const remove = useCallback((id: string) => {
    setData((prevState) => prevState.filter((e) => e.id !== id));
  }, []);

  const value = useMemo(() => ({ data, push: Push, remove }), [data, Push, remove]);

  return (
    <ToastContext.Provider value={value}>
      {createPortal(<ToastContainer />, document.body as Element)}
      {children}
    </ToastContext.Provider>
  );
};
